import React, { useEffect, useRef, useState } from "react";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { ClaimProfesionalDto } from "../../types";
import { Button, Col, Form, ListGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import './../../pages/ClaimStyles.css';
import { getProfessionalsBySearchText } from "../../../profesionals/api/professionalAPI";
import { ProfessionalFormDto } from "../../../profesionals/types";
import ClaimProfessionalForm from "./ClaimProfessionalForm";

interface ProfessionalsSectionProps {
    professionals: ClaimProfesionalDto[];
    onProfessionalsChange: (
        professionals: ClaimProfesionalDto[]
    ) => void;
    readOnly: boolean;
    serviceIndicators?: SelectItemDto[];
    claimProfessionalTypes?: SelectItemDto[];
}

export default function ClaimProfessionalsSection({
    professionals = [],
    readOnly = false,
    serviceIndicators,
    claimProfessionalTypes,
    onProfessionalsChange
}: ProfessionalsSectionProps) {
    const { t } = useTranslation('claim');

    const [professionalsSearch, setProfessionalsSearch] = useState<ClaimProfesionalDto[]>([]);
    const [newProfessional, setNewProfessional] = useState<ClaimProfesionalDto | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);

  
    const getServiceDescription = (id?: number | null): string =>
        id == null
            ? ""
            : serviceIndicators?.find((s) => s.id === id)?.name ?? "";

    const getClaimProfessionalTypesDescription = (id?: number | null): string =>
        id == null
            ? ""
            : claimProfessionalTypes?.find((s) => s.id === id)?.name ?? "";



    const onFieldChange = (
        index: number,
        field: keyof ClaimProfesionalDto,
        value: ClaimProfesionalDto[keyof ClaimProfesionalDto]
    ): void => {
        const updatedProfessionals: ClaimProfesionalDto[] = [...(professionals ?? [])];

        updatedProfessionals[index] = {
            ...updatedProfessionals[index],
            [field]: value
        };

        onProfessionalsChange(updatedProfessionals);
    };

    const onAdd = () => {
        setNewProfessional({
            id: 0,
            professionalId: 0,
            nameAndSurname: "",
            registeredNumber: "",
            email: "",
            serviceIndicatorId: null,
            typeProfessionalId: null,
            claimProfessionalRole: null
        });
    };

    const onNewProfessionalFieldChange = (
        field: keyof ClaimProfesionalDto,
        value: ClaimProfesionalDto[keyof ClaimProfesionalDto]
    ) => {

        if (field === "nameAndSurname" && nameError) {
            setNameError(null); 
        }

        setNewProfessional(prev => {
            if (!prev) return null;

            return {
                ...prev,
                [field]: value
            };
        });
    };

    const onSaveNewProfessional = () => {
        if (!newProfessional) {
            return;
        }

        const name = newProfessional.nameAndSurname?.trim();

        if (!name) {
            setNameError(t("common:form.requiredFieldForm"));
            return;
        }

        onProfessionalsChange([
            ...(professionals ?? []),
            newProfessional
        ]);

        setNewProfessional(null);
    };

    const onRemove = (index: number) => {
        onProfessionalsChange(professionals.filter((_, i) => i !== index));
    };

    const onCancel = () => {
        setNewProfessional(null);
        setNameError(null);
    }

    const handleProfessionalClick = (professional: ProfessionalFormDto) => {

        const newProfessional: ClaimProfesionalDto = {
            id: 0,
            professionalId: professional.id,
            nameAndSurname: professional.nameAndSurname,
            serviceIndicatorId: professional.serviceIndicatorId,
            registeredNumber: professional.registeredNumber,
            typeProfessionalId: professional.typeProfessionalId,
            email: professional.email,
            claimProfessionalRole: null
        };

        onProfessionalsChange([
            ...professionals,
            newProfessional
        ]);

        setQuery("");
        setOpen(false);
    };

    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const comboRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {

        let cancelled = false;
        const fetchData = async () => {
            try {
                const result = await getProfessionalsBySearchText({ text: query });
                const filteredResult = result.filter(
                    p => !professionals.some(
                        professional => professional.professionalId === p.id
                    )
                );

                if (!cancelled) setProfessionalsSearch(filteredResult);
            } catch (err) {
                if (!cancelled) setProfessionalsSearch([]);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [query]);

    return (
        <div className="add-zone">
            <Row >
                <h3>{t("claim:newClaimInputProfessionalTitle")}</h3>
                <p className="hint">{t("claim:newClaimInputProfessionalSubtitle")}</p>
            </Row >
            {(professionals ?? [])
                .filter((p): p is ClaimProfesionalDto => p != null)
                .map((professional: ClaimProfesionalDto, index: number) => {
                    return (
                        <React.Fragment key={index}>
                            <ClaimProfessionalForm
                                professional={professional}
                                readOnly={readOnly}
                                serviceIndicators={serviceIndicators}
                                claimProfessionalTypes={claimProfessionalTypes}
                                onChange={(field, value) =>
                                    onFieldChange(index, field, value)
                                }
                                onRemove={() => onRemove(index)}
                                canRemove={true}
                            />
                        </React.Fragment>
                    );
                })}
            <Row className="claim-professional-container">
                <div className="combo" ref={comboRef}>
                    <div className="combo-input-row">
                        <Form.Control
                            type="text"
                            placeholder={t("claim:newClaimInputProfessionalSearchPlaceholder")}
                            autoComplete="off"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setOpen(true);
                            }}
                            onFocus={() => setOpen(true)}
                        />
                    </div>

                    {open && (
                        <ListGroup className="dropdown">
                            {professionalsSearch.map((p) => (
                                <ListGroup.Item key={p.id ?? p.nameAndSurname} action onClick={() => handleProfessionalClick(p)} >
                                    <div className="opt">
                                        <div className="name">{p.nameAndSurname}</div>
                                        <div className="meta">
                                            {getServiceDescription(p.serviceIndicatorId)} &middot;
                                            Col. {p.registeredNumber} &middot;
                                            {getClaimProfessionalTypesDescription(p.typeProfessionalId)}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}

                    <div className="divider-or"></div>
                </div>
            </Row>

            
            {!readOnly && (
                <Row className="mt-4">
                    <Col md={12} className="text-end">
                        <Button
                            variant="secondary"
                            onClick={onAdd}
                            disabled={newProfessional !== null}
                        >
                            {t("claim:buttonNewProfessional")}
                        </Button>
                    </Col>
                </Row>
            )}

            <Row>
                {newProfessional && (
                    <ClaimProfessionalForm
                        professional={newProfessional}
                        serviceIndicators={serviceIndicators}
                        claimProfessionalTypes={claimProfessionalTypes}
                        onChange={onNewProfessionalFieldChange}
                        onCancel={onCancel}
                        onSave={onSaveNewProfessional}
                        nameError={nameError} 
                    />
                )}
            </Row>
        </div>
    );
}